/**
 * Call Change Proposals review (#312, increment B).
 *
 * Renders on the event workspace whenever a phone call proposed edits to
 * this Booking (or an ambiguous call names it as a candidate), and
 * disappears once every proposal is decided. Proposals are resolved by
 * action (accept/reject); an ambiguous call additionally carries an X —
 * "not this event" — which drops this Booking from its candidate set
 * server-side. Each card carries the transcript span that produced it:
 * the Operator judges the edit by what was said, and accepts or rejects
 * per proposal. Accept is the only write; a 409 means the field moved on
 * since the call and the row reports it inline.
 */
import { useCallback, useEffect, useState } from "react";
import { cx } from "@shared/cx";
import styles from "./CallProposalsSection.module.css";
import { Button } from "@/components/ui/Button";
import { InlineBanner } from "@/components/ui/InlineBanner";
import { XIcon } from "@/components/ui/icons";
import {
  chooseCallBooking,
  decideCallProposal,
  dismissCallBooking,
  listCallProposals,
  type CallProposal,
  type NeedsBookingCall,
} from "@/lib/call-proposals-api";

const KIND_LABELS: Record<string, string> = {
  set_line_qty: "Change quantity",
  remove_line: "Remove line",
  add_line: "Add line",
  append_note: "Add job note",
};

const TARGET_LABELS: Record<string, string> = {
  event_date: "Event date",
  event_end_date: "Event end date",
  event_time: "Event time",
  location: "Venue",
  attendance: "Guests",
  venue_contact: "Venue contact",
  client_name: "Customer name",
  client_email: "Customer email",
  client_phone: "Customer phone",
};

function proposalLabel(p: CallProposal): string {
  if (p.kind === "set_booking_field" || p.kind === "set_customer_field") {
    return TARGET_LABELS[p.target] ?? p.target;
  }
  return KIND_LABELS[p.kind] ?? p.kind;
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value === "" ? "—" : value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  const obj = value as { name?: unknown; quantity?: unknown };
  if (typeof obj.name === "string") {
    const qty = typeof obj.quantity === "number" ? `${obj.quantity}× ` : "";
    return `${qty}${obj.name}`;
  }
  return JSON.stringify(value);
}

export function CallProposalsSection({
  bookingId,
  onApplied,
}: {
  bookingId: string;
  /** Called after an accepted proposal changes the Booking, so the
   *  workspace refetches what the edit touched. */
  onApplied: () => void;
}) {
  const [proposals, setProposals] = useState<CallProposal[]>([]);
  const [ambiguousCalls, setAmbiguousCalls] = useState<NeedsBookingCall[]>([]);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [queuedCallIds, setQueuedCallIds] = useState<string[]>([]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const res = await listCallProposals(bookingId, signal);
        setProposals(res.proposals);
        setAmbiguousCalls(res.needsBookingCalls);
      } catch {
        // A failed read renders nothing: this surface is additive, and
        // the workspace's own load error already reports page trouble.
      }
    },
    [bookingId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // While a re-queued call is being read, refetch every few seconds —
  // the choose endpoint dispatches the worker directly, so proposals
  // land in seconds, not on the next page load. Capped so a call that
  // resolved away from this event (it was a new enquiry after all)
  // does not poll forever.
  useEffect(() => {
    if (queuedCallIds.length === 0) return;
    const controller = new AbortController();
    const startedAt = Date.now();
    const tick = setInterval(() => {
      if (Date.now() - startedAt > 120_000) {
        setQueuedCallIds([]);
        return;
      }
      void load(controller.signal);
    }, 3_000);
    return () => {
      clearInterval(tick);
      controller.abort();
    };
  }, [queuedCallIds, load]);

  // A queued call is done once its proposals show up.
  useEffect(() => {
    if (queuedCallIds.length === 0 || proposals.length === 0) return;
    const resolved = new Set(proposals.map((p) => p.phone_call_id));
    setQueuedCallIds((prev) => {
      const next = prev.filter((id) => !resolved.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [proposals, queuedCallIds]);

  const decide = async (id: string, action: "accept" | "reject") => {
    setBusyId(id);
    setRowErrors((prev) => ({ ...prev, [id]: "" }));
    try {
      await decideCallProposal(id, action);
      setProposals((prev) => prev.filter((p) => p.id !== id));
      if (action === "accept") onApplied();
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [id]: err instanceof Error ? err.message : "Failed to save",
      }));
      // A 409 means the proposal is superseded/decided server-side —
      // re-read so the list reflects the truth.
      void load();
    } finally {
      setBusyId(null);
    }
  };

  const acceptAll = async () => {
    setAcceptingAll(true);
    for (const p of proposals) {
      // Sequential on purpose: stops at nothing, but each conflict
      // surfaces on its own row rather than aborting the batch.
      // eslint-disable-next-line no-await-in-loop
      await decide(p.id, "accept");
    }
    setAcceptingAll(false);
  };

  const attachCall = async (call: NeedsBookingCall) => {
    setBusyId(call.id);
    setRowErrors((prev) => ({ ...prev, [call.id]: "" }));
    try {
      await chooseCallBooking(call.id, bookingId);
      setAmbiguousCalls((prev) => prev.filter((c) => c.id !== call.id));
      setQueuedCallIds((prev) => [...prev, call.id]);
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [call.id]: err instanceof Error ? err.message : "Failed to save",
      }));
    } finally {
      setBusyId(null);
    }
  };

  const dismissCall = async (call: NeedsBookingCall) => {
    setBusyId(call.id);
    setRowErrors((prev) => ({ ...prev, [call.id]: "" }));
    try {
      await dismissCallBooking(call.id, bookingId);
      setAmbiguousCalls((prev) => prev.filter((c) => c.id !== call.id));
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [call.id]: err instanceof Error ? err.message : "Failed to save",
      }));
    } finally {
      setBusyId(null);
    }
  };

  if (
    proposals.length === 0 &&
    ambiguousCalls.length === 0 &&
    queuedCallIds.length === 0
  ) {
    return null;
  }

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Phone call changes</h2>
          <p className={styles.subtitle}>
            Proposed from what was said on the call. Nothing is applied until
            you accept it.
          </p>
        </div>
        {proposals.length > 1 && (
          <Button
            size="sm"
            variant="secondary"
            loading={acceptingAll}
            onClick={() => void acceptAll()}
          >
            Accept all
          </Button>
        )}
      </div>

      {proposals.length > 0 && (
        <ul className={styles.list}>
          {proposals.map((p) => (
            <li key={p.id} className={styles.row}>
              <div className={styles.rowBody}>
                <div className={styles.rowText}>
                  <p className={styles.proposalLabel}>{proposalLabel(p)}</p>
                  <p className={styles.values}>
                    {p.current !== null && p.current !== undefined && (
                      <>
                        <span className={styles.currentValue}>
                          {renderValue(p.current)}
                        </span>{" "}
                      </>
                    )}
                    <span className={styles.proposedValue}>
                      {renderValue(p.proposed)}
                    </span>
                  </p>
                  <p className={styles.span}>
                    “{p.transcript_span}” — caller {p.from_e164}
                  </p>
                </div>
                <div className={styles.actions}>
                  <Button
                    size="sm"
                    loading={busyId === p.id}
                    onClick={() => void decide(p.id, "accept")}
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busyId === p.id}
                    onClick={() => void decide(p.id, "reject")}
                  >
                    Reject
                  </Button>
                </div>
              </div>
              {rowErrors[p.id] ? (
                <InlineBanner variant="error" placement="none">
                  {rowErrors[p.id]}
                </InlineBanner>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {ambiguousCalls.length > 0 && (
        <ul className={styles.callList}>
          {ambiguousCalls.map((c) => (
            <li key={c.id} className={styles.row}>
              <div className={cx(styles.rowBody, styles.rowBodyCentred)}>
                <div>
                  <p className={styles.proposalLabel}>
                    A call from {c.from_e164} matches several jobs
                  </p>
                  <p className={styles.subtitle}>
                    If it was about this event, its changes are read once you
                    confirm.
                  </p>
                </div>
                <div className={styles.actionsCentred}>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={busyId === c.id}
                    onClick={() => void attachCall(c)}
                  >
                    It was about this event
                  </Button>
                  <button
                    type="button"
                    aria-label="Not this event"
                    disabled={busyId === c.id}
                    className={styles.dismiss}
                    onClick={() => void dismissCall(c)}
                  >
                    <XIcon className={styles.dismissIcon} />
                  </button>
                </div>
              </div>
              {rowErrors[c.id] ? (
                <InlineBanner variant="error" placement="none">
                  {rowErrors[c.id]}
                </InlineBanner>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {queuedCallIds.length > 0 && (
        <p className={styles.waiting}>
          Reading the call against this event — proposed changes appear here in
          a few seconds.
        </p>
      )}
    </section>
  );
}
